import { INDEX_TABLE_SELECT_ALL_ITEMS, type ResourceListProps } from '@shopify/polaris';
import { useCallback, useState } from 'react';

type ISelectedItems = string[];

interface IAssetTemplatePickerProps {
  isOpenModalMultiple: boolean;
}
export interface IAssetTemplatePickerResult {
  selectedItems?: ISelectedItems;
  setSelectedItems: (value?: ISelectedItems) => void;
  handleSetSelectedItem: (newItems: ResourceListProps['selectedItems']) => void;
  handleSetSelectedItems: (newItems?: ResourceListProps['selectedItems']) => void;
}

export const useInitializeAssetTemplatePicker = (
  props: IAssetTemplatePickerProps,
): IAssetTemplatePickerResult => {
  const { isOpenModalMultiple } = props;

  const [selectedItems, setSelectedItems] = useState<ISelectedItems | undefined>();

  const handleSetSelectedItem = useCallback(
    (newItems: ResourceListProps['selectedItems']) => {
      setSelectedItems((prev) => {
        if (!newItems?.length) return prev;
        if (newItems === INDEX_TABLE_SELECT_ALL_ITEMS) return prev;
        if (!isOpenModalMultiple) return newItems;

        const newItemsArray = newItems as string[];

        if (Array.isArray(prev)) {
          const prevSet = new Set(prev);
          const updatedItems = newItemsArray.reduce((acc, item) => {
            if (prevSet.has(item)) {
              acc.delete(item);
            } else {
              acc.add(item);
            }
            return acc;
          }, prevSet);

          return [...updatedItems];
        }

        return [...new Set(newItemsArray)];
      });
    },
    [isOpenModalMultiple],
  );

  const handleSetSelectedItems = useCallback(
    (newItems?: ResourceListProps['selectedItems']) => {
      if (newItems === INDEX_TABLE_SELECT_ALL_ITEMS) return;
      if (!newItems?.length) return setSelectedItems(undefined);
      if (!isOpenModalMultiple) {
        const data = newItems[newItems.length - 1];
        return data && handleSetSelectedItem([data]);
      }

      setSelectedItems(newItems);
    },
    [handleSetSelectedItem, isOpenModalMultiple],
  );

  return {
    selectedItems,
    setSelectedItems,
    handleSetSelectedItem,
    handleSetSelectedItems,
  };
};
